import type { ContourOverride, ParamStore, ParamValue } from './nodeDefs.js';

export type ParamGraphSlot = 'geometry' | 'material' | 'layout';

export function normalizeGraphScope(graphId: string | null | undefined, slot: ParamGraphSlot = 'geometry'): string {
  if (!graphId) return `component_main:${slot}`;
  return graphId.includes(':') ? graphId : `${graphId}:${slot}`;
}

function getLegacyScopedParamKey(graphId: string | null | undefined, nodeId: string, slot: ParamGraphSlot): string | null {
  if (!graphId || graphId.includes(':')) return null;
  return `param:layer:${graphId}:${slot}:${nodeId}`;
}

export function scopedParamKey(graphId: string | null | undefined, nodeId: string, slot: ParamGraphSlot = 'geometry'): string {
  return `param:${normalizeGraphScope(graphId, slot)}:${nodeId}`;
}

export function readParamValue(
  graphId: string | null | undefined,
  params: ParamStore,
  nodeId: string,
  fallback: ParamValue,
  slot: ParamGraphSlot = 'geometry',
): ParamValue {
  const key = scopedParamKey(graphId, nodeId, slot);
  if (params[key] !== undefined) return params[key];
  const legacyKey = getLegacyScopedParamKey(graphId, nodeId, slot);
  if (legacyKey && params[legacyKey] !== undefined) return params[legacyKey];
  return fallback;
}

/** `geometry:<node>.contours` override: a non-empty array of contour objects (`anchors` numeric). */
export function isContourOverrideArray(value: unknown): value is ContourOverride[] {
  return Array.isArray(value)
    && value.length > 0
    && value.every(item => typeof item === 'object' && item !== null && Array.isArray((item as ContourOverride).anchors));
}

/**
 * Scene-node `params` over a base store (`mergeLayerParams` in the engine's render crate):
 * numeric coercion for vectors/scalars, strings and contour arrays pass through untouched.
 */
export function mergeParamOverrides(base: ParamStore, overrides: Record<string, unknown>): ParamStore {
  const merged: ParamStore = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    merged[key] = isContourOverrideArray(value)
      ? value
      : Array.isArray(value)
        ? value.map(entry => Number(entry))
        : typeof value === 'string'
          ? value
          : Number(value);
  }
  return merged;
}
